import { useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { useCharacterNames } from "src/hooks/useCharacterNames";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip/index.tsx";
import { formatEstimatedCreditCost } from "src/shared/creditEstimate";
import { GET_POV_ABBREVATION, extractFirstName, isAutoPOV, isAutoTense } from "../../../../../../src/shared/constants";
import { UNLIMITED_EXPERIMENT_VARIATIONS } from "../../../../../../src/shared/constants.js";
import { LeftSide, PreflightContainer, RightSide, SuggestionLink } from "./styles";

// Helper function to format numbers with locale-specific thousands separators
const formatNumber = (value) => {
  if (!value || isNaN(value)) return;
  if (typeof value !== "number") return value;
  return value.toLocaleString();
};

function PreflightAnalysis({ preflightResponse, documentScenes, rewriteScenes, retryPreflight, proseMode, hasAccessToPOVAndTenseBeta, povTense, missingScenesAndNotConnected, isPreflightRunning, children }) {
  // Fallback calculation if API estimate isn't available
  const APPX_WORDS_GENERATED_PER_SCENE = proseMode?.wordsPerScene || 500;
  const localEstimatedWordCount = documentScenes?.value?.scenes?.content[0]?.content?.length * APPX_WORDS_GENERATED_PER_SCENE;
  const [isRewriting, setIsRewriting] = useState(false);
  // Unlimited Experiment
  const isInUnlimitedExperiment = useSudowriteStore((state) => state.isInUnlimitedExperiment);
  const unlimitedExperimentVariation = useSudowriteStore((state) => state.unlimitedExperimentVariation);
  const characterNames = useCharacterNames(povTense.pov.characters);

  const isFree = () => {
    return (
      (proseMode?.muse2Alpha && !proseMode?.benchmarkMode) ||
      (isInUnlimitedExperiment && unlimitedExperimentVariation !== UNLIMITED_EXPERIMENT_VARIATIONS.CONTROL && proseMode.key === "BASIC")
    );
  };

  const { ResponseType, Diagnosis, QuickFixCTA, ApprovalMessage, RewritePrompt, estimates } = preflightResponse || {};
  const { wordCountEstimate, estimatedCreditCost, creditCostEstimateRange } = estimates || {};
  const recursionRisk = estimates?.recursionRisk;

  const diagnosisWithPeriod = useMemo(() => {
    if (!Diagnosis) return "";
    const trimmedDiagnosis = Diagnosis.trim();
    if (!trimmedDiagnosis) return "";
    return /[.?!]$/.test(trimmedDiagnosis) ? trimmedDiagnosis : `${trimmedDiagnosis}.`;
  }, [Diagnosis]);

  // Use API estimate if available, otherwise fall back to local calculation
  let displayWordCount = formatNumber(localEstimatedWordCount);

  if (wordCountEstimate) {
    if (wordCountEstimate.p25 && wordCountEstimate.p50 && wordCountEstimate.p75) {
      // Show range with all three percentiles in a clearer format
      displayWordCount = `${formatNumber(wordCountEstimate.p25)} - ${formatNumber(wordCountEstimate.p75)}`;
    } else if (wordCountEstimate.p25 && wordCountEstimate.p75) {
      // Fallback to just p25-p75 range
      displayWordCount = `${formatNumber(wordCountEstimate.p25)} - ${formatNumber(wordCountEstimate.p75)}`;
    } else if (wordCountEstimate.median) {
      // Last fallback to median if that's all we have
      displayWordCount = `${formatNumber(wordCountEstimate.median)}`;
    }
  }

  // Prepare credit cost display - use range if available, otherwise fallback to single value
  const displayCreditCost = formatEstimatedCreditCost({
    estimatedCreditCost,
    creditCostEstimateRange,
  }) ?? formatNumber(estimatedCreditCost);

  // Tooltip content for the estimates
  const tooltipContent = useMemo(() => {
    if (missingScenesAndNotConnected) {
      return null;
    }

    return <div>This is an estimate based on your document, Story Bible, number of scenes, and Prose Mode.</div>;
  }, [missingScenesAndNotConnected]);

  const isFreeEstimate = estimates?.novelNovemberSprint || isFree();
  const showCreditCost = isFreeEstimate || displayCreditCost;
  const povIsNone = isAutoPOV(povTense?.pov?.value);
  const tenseIsNone = isAutoTense(povTense?.tense?.value);

  return (
    <PreflightContainer isPreflightRunning={isPreflightRunning}>
      <LeftSide isPreflightRunning={isPreflightRunning}>
        <div className="flex flex-col gap-2">
          {missingScenesAndNotConnected && !isPreflightRunning && (
            <div className="feedback-tag items-start">
              <SVG
                src="/assets/warning-square.svg"
                alt="Suggestion"
                className="warning"
              />
              <span>There are no scenes, so Sudowrite may produce unexpected results. Add some scenes for best results.</span>
            </div>
          )}
          {recursionRisk && !isPreflightRunning && (
            <div className="feedback-tag items-start">
              <SVG
                src="/assets/warning-square.svg"
                alt="Warning"
                className="warning"
              />
              <span>{recursionRisk.message}</span>
            </div>
          )}
          {hasAccessToPOVAndTenseBeta && !isPreflightRunning && (
            <div className="feedback-tag feedback-estimates">
              {povIsNone || tenseIsNone ? (
                <SVG
                  src="/assets/warning-square.svg"
                  alt="Suggestion"
                  className="warning"
                />
              ) : (
                <SVG
                  src="/assets/check-simple.svg"
                  alt="Approved"
                  className="check-approved"
                />
              )}
              <div className="estimates-content flex flex-col gap-2 items-start">
                <span>
                  {povIsNone ? (
                    <span className="mx-0">No POV selected: may result in inconsistent POV. </span>
                  ) : (
                    <>
                      <span className="mx-0">
                        {GET_POV_ABBREVATION(povTense.pov.value)} {characterNames.length > 0 ? ` - ${characterNames.map(extractFirstName).join(", ")} ` : ""}
                      </span>
                    </>
                  )}{" • "}
                  {tenseIsNone ? <span className="mx-0">No tense selected: may result in inconsistent tense.</span> : <span className="capitalize mx-0">{povTense.tense.value} tense.</span>}
                </span>
              </div>
            </div>
          )}
          {preflightResponse && ResponseType !== "Error" && (
            <SudoTooltip
              text={"This is an estimate based on your document, Story Bible, number of scenes, and Prose Mode."}
              tooltipClassName="w-[300px] max-w-[90vw]"
              triggerClassName="w-full"
            >
              <div className="feedback-tag feedback-estimates">
                <SVG
                  src="/assets/check-simple.svg"
                  alt="Approved"
                  className="check-approved"
                />
                <div className="estimates-content">
                  {displayWordCount && (
                    <>
                      <span>Estimated words: {displayWordCount}</span>
                      {showCreditCost && <span>•</span>}
                    </>
                  )}
                  {showCreditCost && <span>Estimated credits: {isFreeEstimate ? "Free" : displayCreditCost}</span>}
                </div>
              </div>
            </SudoTooltip>
          )}

        </div>

        {preflightResponse && !missingScenesAndNotConnected && (
          <div className="feedback-tag items-center">
            {ResponseType === "Approval" && (
              <>
                <SVG
                  src="/assets/check-simple.svg"
                  alt="Approved"
                  className="check-approved"
                />
                <span>{ApprovalMessage}</span>
              </>
            )}
            {ResponseType === "Suggestion" && (
              <>
                <SVG
                  src="/assets/warning-square.svg"
                  alt="Suggestion"
                  className="warning"
                />
                <span>
                  {diagnosisWithPeriod} {QuickFixCTA && `Quick Fix: `}
                  {QuickFixCTA && (
                    <SuggestionLink
                      href="#"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isRewriting) return;
                        setIsRewriting(true);
                        await rewriteScenes(RewritePrompt);
                        setIsRewriting(false);
                      }}
                    >
                      {!isRewriting ? QuickFixCTA.replace(/ /g, "\u00A0") : "Rewriting..."}
                    </SuggestionLink>
                  )}
                </span>
              </>
            )}
            {ResponseType === "Error" && (
              <>
                <SVG
                  src="/assets/warning-square.svg"
                  alt="Error"
                  className="warning"
                />
                <span>
                  {diagnosisWithPeriod}{" "}
                  {QuickFixCTA === "Retry" && retryPreflight && (
                    <SuggestionLink
                      href="#"
                      aria-disabled={isPreflightRunning}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isPreflightRunning) return;
                        retryPreflight();
                      }}
                    >
                      {isPreflightRunning ? "Retrying…" : "Retry"}
                    </SuggestionLink>
                  )}
                </span>
              </>
            )}
          </div>
        )}
      </LeftSide>
      <RightSide isPreflightRunning={isPreflightRunning}>{children}</RightSide>
    </PreflightContainer>
  );
}

export default PreflightAnalysis;
